import VStack from "../../../ui_atoms/VStack";
import { BodyMediumText } from "../../FormattedText";
import MissionInfoItem from "../../MissionInfoItem";
import ListItem from "../../../ui_atoms/ListItem";
import Label from "../../../ui_atoms/Label";
import React, { use, useMemo } from "react";
import { PayrollSourceType, ProfileType, TimeSheetState } from "../../../__generated__/graphql";
import { MissionPayrollBreakdownFieldsFragment } from "../../../__generated__/operations/graphql";
import { useTheme } from "../../../hooks/use-theme";
import { useI18n } from "../../../hooks/i18n-hooks";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../../navigations/RootNavigation";
import { useNavigation } from "@react-navigation/native";
import { useScrollHeaderColorSync } from "../../../hooks/use-scroll-header-color-sync";
import Loading from "../../Loading";
import { ProfileSwitcherManagerContext } from "../../../contexts/ProfileSwitcherManager";

type PayrollBreakdownInfoProps = {
    payrollBreakdown?: MissionPayrollBreakdownFieldsFragment;
    introSentence: string;
};

type ScreenParams = StackScreenProps<RootStackParamList>;

const PayrollBreakdownInfo: React.FC<PayrollBreakdownInfoProps> = ({
    payrollBreakdown,
    introSentence,
}) => {
    const { colors } = useTheme();
    const i18n = useI18n();
    const navigator = useNavigation<ScreenParams["navigation"]>();
    const { profileType } = use(ProfileSwitcherManagerContext);

    //no timesheets are considered as completed as for monthly mission we can have the first month without any timesheet.
    const allCompleted = !!(
        payrollBreakdown?.timesheets.length === 0 ||
        payrollBreakdown?.timesheets?.every(
            (ts) => ts.status === TimeSheetState.Submitted || ts.status === TimeSheetState.Accepted,
        )
    );
    const allAccepted = useMemo(
        () => payrollBreakdown?.timesheets?.every((ts) => ts.status === TimeSheetState.Accepted),
        [payrollBreakdown?.timesheets],
    );
    const isFromTimesheet = useMemo(() => {
        return payrollBreakdown?.missionInfo.payrollSource === PayrollSourceType.Lift;
    }, [payrollBreakdown]);

    const { limitComponent } = useScrollHeaderColorSync({
        aboveLimitColor: colors.accentLighter,
        belowLimitColor: "white",
    });
    if (!payrollBreakdown) {
        return <Loading />;
    }
    return (
        <>
            <VStack v="2/3" h="2/3" style={{ backgroundColor: colors.accentLighter }}>
                <BodyMediumText>{introSentence}</BodyMediumText>
            </VStack>
            <MissionInfoItem
                backgroundColor={colors.accentLighter}
                payrollBreakdown={payrollBreakdown}
            />
            {isFromTimesheet && (
                <ListItem
                    style={{ backgroundColor: colors.accentLighter }}
                    icon="timesheet"
                    description={i18n.t("screens.payroll_breakdown.show_timesheets_action")}
                    labels={
                        !allAccepted && profileType === ProfileType.Client
                            ? [
                                  {
                                      mode: "miniCtaBold",
                                      text: i18n.t("screens.payroll_breakdown.to_review"),
                                  },
                              ]
                            : []
                    }
                    badge={
                        !allCompleted ? (
                            <Label
                                mode="warning"
                                text={i18n.t(
                                    "screens.payroll_breakdown_editor.incomplete_timesheets",
                                )}
                            />
                        ) : undefined
                    }
                    onPress={() => {
                        navigator.navigate("PayrollBreakdownTimeSheetScreen", {
                            payrollBreakdownId: payrollBreakdown.id,
                        });
                    }}
                />
            )}
            {limitComponent}
        </>
    );
};

export default PayrollBreakdownInfo;
